import { type FC, Fragment, useCallback, useEffect, useMemo } from 'react';

import { httpDeleteCollectionsByCollectionIdContentsByObjectTypeByObjectId } from '@iconik-io/iconik-api/src/services/assets';
import {
  FIELD_KEYFRAMES,
  FIELD_MEDIA_TYPE,
  FIELD_TITLE,
  FIELD_TYPES,
} from '@iconik-io/shared-lib/src/constants/search';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import Ellipsis from '../../../components/ellipsis/ellipsis';
import { Link } from '../../../components/link/link';
import TooltipTrigger from '../../../components/tooltip_trigger/tooltip_trigger';
import { assetRelationDeleted } from '../../../ducks/asset_relations/slice';
import type { CollectionTitleObject } from '../../../ducks/collections/selectors';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import Button from '../../../ui/button/button';
import TableView from '../../../ui/table/table';
import type { Column } from '../../../ui/table/types';
import { isCollectionMappedToStorage } from '../../../utils/collections';
import { selectUserRoles } from '../../../utils/get_user_roles';

import styles from '../asset_relations.module.css';

export type CollectionRelation = SearchItemEntity & {
  breadcrumbs: CollectionTitleObject[];
};

type Props = {
  items: CollectionRelation[];
  sourceId: string;
  newInCollectionsIds?: string[];
  label: string;
};

const CollectionRelationBreadcrumbs: FC<{ item: CollectionRelation }> = ({
  item,
}) => {
  // Tooltip needs to be constructed separately as a string otherwise it won't render correctly in the tooltip.
  const tooltip = item.breadcrumbs
    .map((breadcrumb) => breadcrumb.title)
    .join(' > ');
  const breadcrumbs = item.breadcrumbs.map(({ id, title }, index) => (
    <Fragment key={id}>
      <Link to={`/collection/${id}`}>{title}</Link>
      {index < item.breadcrumbs.length - 1 && ' > '}
    </Fragment>
  ));
  return (
    <Ellipsis
      componentProps={{ className: styles.collection_path }}
      tooltipText={tooltip}
    >
      {breadcrumbs}
    </Ellipsis>
  );
};

const CollectionRelationsTable: FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const breakpoint = useAppSelector((state) => state.displayReducer.breakpoint);
  const isAdmin = useAppSelector(
    (state) => state.authReducer.userData.is_admin,
  );
  const userRoles = useAppSelector(selectUserRoles);

  useEffect(() => {
    if (props.newInCollectionsIds?.length) {
      const updatedItem = document.querySelector(
        `[data-id='${
          props.newInCollectionsIds[props.newInCollectionsIds.length - 1]
        }']`,
      );
      if (updatedItem) {
        updatedItem.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [props.newInCollectionsIds]);

  const handleDelete = useCallback(
    (collectionId: string, assetId: string) =>
      dispatch(
        httpDeleteCollectionsByCollectionIdContentsByObjectTypeByObjectId(
          collectionId,
          'assets',
          assetId,
        ),
      ).then(() => {
        dispatch(
          assetRelationDeleted({
            target: collectionId,
            source: assetId,
            relation: 'COLLECTION',
          }),
        );
      }),
    [],
  );

  const columns = useMemo(() => {
    const renderColumns: Column<CollectionRelation>[] = [];

    renderColumns.push({
      name: FIELD_KEYFRAMES,
      type: FIELD_TYPES.THUMBNAIL,
    });

    renderColumns.push({
      name: FIELD_TITLE,
      type: FIELD_TYPES.CUSTOM_COMPONENT,
      component: ({ item }) => (
        <div className={styles.cell}>
          <Ellipsis
            component={Link}
            componentProps={{
              className: styles.link,
              to: `/collection/${item.id}`,
              'aria-label': 'Collection relation link',
            }}
            tooltipText={item.title}
          >
            {item.title}
          </Ellipsis>
          <CollectionRelationBreadcrumbs item={item} />
        </div>
      ),
      style: { width: '70%', maxWidth: '100px' },
    });

    if (breakpoint >= 3) {
      renderColumns.push({
        name: FIELD_MEDIA_TYPE,
        type: FIELD_TYPES.MEDIA_ICON,
        style: { width: '1%' },
      });
    }

    renderColumns.push({
      name: 'id',
      type: FIELD_TYPES.CUSTOM_COMPONENT,
      component: ({ item }) => {
        const relationToMappedCollection = isCollectionMappedToStorage(item);
        const buttonDisabled =
          (!item.permissions?.includes('write') ||
            (props.items.length === 1 && !userRoles.webCanTopLevelUpload)) &&
          !isAdmin;
        return (
          <TooltipTrigger
            tooltipText={'No permissions'}
            placement="left"
            disabled={!buttonDisabled}
          >
            <Button
              aria={{ label: 'Delete relation' }}
              disabled={buttonDisabled || relationToMappedCollection}
              variant="danger"
              noBorder
              wideBasis={false}
              iconName="deletion_delete"
              onClick={() => {
                handleDelete(item.id, props.sourceId);
              }}
            />
          </TooltipTrigger>
        );
      },
      style: { width: '10%' },
    });

    return renderColumns;
  }, [
    props.sourceId,
    breakpoint,
    isAdmin,
    props.items,
    handleDelete,
    userRoles,
  ]);

  return (
    <TableView
      items={props.items}
      renderColumns={columns}
      showHeader={false}
      flashHighlightIds={props.newInCollectionsIds}
      aria={{
        label: `${props.label} relations`,
      }}
    />
  );
};

export default CollectionRelationsTable;
